import React, {useState, useEffect, useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {API} from 'aws-amplify';
import queryString from 'query-string';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faObjectsColumn} from '@fortawesome/pro-solid-svg-icons';

import AuthService from '../auth/services/AuthService';
import AnalyticsService from '../services/AnalyticsService';
import PrimaryAppBar from './PrimaryAppBar';
import PDFViewer from './PDFViewer';
import WordWorkApp from '../word-work/components/WordWorkApp';
import WordSortApp from '../word-sort/components/WordSortApp';
import WordDivisionApp from '../word-division/components/WordDivisionApp';
import Writing from './Writing';
import {
  reset,
  selectActiveExercise,
  selectSteps,
  setAssignmentLesson,
  setAssignmentExercise,
  selectExercise,
} from '../redux/slices/lesson';
import {selectUser} from '../redux/slices/user';
import {
  APP_WORD_WORK,
  APP_WORD_SORT,
  APP_WORD_DIVISION,
  APP_READING,
  APP_WRITING,
  APP_ASSESSMENT,
  ENDPOINT,
} from '../constants';
import {getRandomInt} from '../helpers/numberHelpers';
import {aOrAn} from '../helpers';

import ImageLogo from '../home/assets/images/logos/flamingo/lockup/dark.svg';
import ImagePenguin from '../word-work/images/clipart/animals/penguin.png';
import ImageWhale from '../word-work/images/clipart/animals/whale.png';
import ImageTiger from '../word-work/images/clipart/animals/tiger.png';
import ImageRhino from '../word-work/images/clipart/animals/rhino.png';
import ImageOctopus from '../word-work/images/clipart/animals/octopus.png';
import ImageOwl from '../word-work/images/clipart/animals/owl.png';
import ImageKoala from '../word-work/images/clipart/animals/koala.png';
import ImageMoose from '../word-work/images/clipart/animals/moose.png';
import ImageMonkey from '../word-work/images/clipart/animals/monkey.png';
import ImagePolarBear from '../word-work/images/clipart/animals/polar-bear.png';

import styles from '../css/AssignmentViewer.module.css';
import baseStyles from '../css/Base.module.css';

const MODE_LOADING = 'LOADING';
const MODE_START = 'START';
const MODE_ASSIGNMENT = 'ASSIGNMENT';
const MODE_COMPLETE = 'COMPLETE';
const PROGRESSES = ['PENDING', 'IN_PROGRESS', 'COMPLETED'];

const ANIMALS_CONFIG = {
  penguin: ImagePenguin,
  whale: ImageWhale,
  tiger: ImageTiger,
  rhino: ImageRhino,
  octopus: ImageOctopus,
  owl: ImageOwl,
  koala: ImageKoala,
  moose: ImageMoose,
  monkey: ImageMonkey,
  'polar bear': ImagePolarBear,
};
const keys = Object.keys(ANIMALS_CONFIG);
const randomIndex = getRandomInt(0, keys.length - 1);
const randomAnimalName = keys[randomIndex];
const randomAnimalImage = ANIMALS_CONFIG[randomAnimalName];

async function updateAssignmentProgress(assignment, authCode, progress) {
  const {isAuthenticated} = await AuthService.checkAuthentication();
  if (!isAuthenticated) {
    // Only update progress for students

    // Analytics
    let analyticsEventType;
    if (progress === 'IN_PROGRESS') analyticsEventType = 'ASSIGNMENT_START';
    else if (progress === 'COMPLETED') analyticsEventType = 'ASSIGNMENT_COMPLETE';
    await AnalyticsService.createEvent(analyticsEventType, {
      assignmentID: assignment.id,
      studentAuthCode: authCode,
    });

    const currentProgressIndex = PROGRESSES.indexOf(assignment.progress);
    const newProgressIndex = PROGRESSES.indexOf(progress);

    if (newProgressIndex > currentProgressIndex) {
      const request = {
        body: {
          progress,
        },
      };
      await API.patch(ENDPOINT, `/assignments/auth-code/${authCode}?assignmentID=${assignment.id}`, request);
    }
  } else {
    console.log('User is authenticated, not updating assignment progress.');
  }
}

export default function AssignmentViewer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const steps = useSelector(selectSteps);
  const activeExercise = useSelector(selectActiveExercise);
  const exercise = useSelector(selectExercise);
  const user = useSelector(selectUser);

  const [mode, setMode] = useState(MODE_LOADING);
  const [assignment, setAssignment] = useState(null);
  const [student, setStudent] = useState(null);

  const queryStrings = queryString.parse(location.search);
  const {authCode, sessionID, sessionIndex = 0, auto = 'false'} = queryStrings;

  const autoPlay = auto === 'true' ? true : false;
  const isLessonComplete = steps.length > 0 && steps.every((step) => step.status === 'COMPLETE');

  useEffect(() => {
    if (sessionID) {
      setMode(MODE_LOADING);
      const fetchData = async () => {
        const assignments = await API.get(
          ENDPOINT,
          `/assignments/auth-code/${authCode}?sessionID=${sessionID}&sessionIndex=${sessionIndex}`,
        );
        const assignment = assignments[0];
        if (assignment) {
          setAssignment(assignment);
          if (assignment.type === 'LESSON') {
            dispatch(setAssignmentLesson(assignment.assignment));
          } else {
            dispatch(setAssignmentExercise(assignment.assignment));
          }

          const student = await API.get(ENDPOINT, `/students/auth-code/${authCode}`);
          setStudent(student);

          setMode(MODE_START);
        } else {
          setMode(MODE_COMPLETE);
        }
      };
      fetchData();
    }
  }, [sessionID, authCode, sessionIndex, dispatch]);

  const handleAssignmentComplete = useCallback(async () => {
    setMode(MODE_LOADING);
    await updateAssignmentProgress(assignment, authCode, 'COMPLETED');
    dispatch(reset());
    if (autoPlay) {
      const nextSessionIndex = parseInt(sessionIndex) + 1;
      const nextAssignmentURL = `/assignment?authCode=${authCode}&sessionID=${sessionID}&sessionIndex=${nextSessionIndex}&auto=true`;
      history.push(nextAssignmentURL);
    } else {
      setMode(MODE_COMPLETE);
    }
  }, [sessionIndex, authCode, sessionID, assignment, history, dispatch, autoPlay]);

  useEffect(() => {
    if (isLessonComplete) {
      handleAssignmentComplete();
    }
  }, [isLessonComplete, handleAssignmentComplete]);

  function handleClickDashboard() {
    history.push(`/student/dashboard?authCode=${authCode}`);
  }

  async function handleClickStart() {
    setMode(MODE_ASSIGNMENT);
    await updateAssignmentProgress(assignment, authCode, 'IN_PROGRESS');
  }

  function renderEmptyAppBar() {
    return (
      <AppBar position="sticky" className="bg-brandorange">
        <Toolbar variant="dense">
          <Typography variant="h6" className={baseStyles['f-g-1']}></Typography>
        </Toolbar>
      </AppBar>
    );
  }

  function renderExerciseAppBar(exercise) {
    return (
      <AppBar position="sticky" className="bg-brandorange">
        <Toolbar variant="dense">
          <Typography variant="h6" className={baseStyles['f-g-1']}>
            {exercise?.name}
          </Typography>

          <Button color="inherit" onClick={handleAssignmentComplete} className={baseStyles['btn-pill']}>
            I&apos;M READY TO MOVE ON
          </Button>
        </Toolbar>
      </AppBar>
    );
  }

  function renderComplete() {
    return (
      <div className={`${styles['center-col']} ${baseStyles['m-t-90']}`}>
        <img src={randomAnimalImage} alt="animal" />
        <Typography variant="h2" className="fnt-bold">
          Great job!
        </Typography>
        <Typography variant="h2" className="fnt-bold">
          You finished your assignments!
        </Typography>
        <Typography variant="h4">{`Here! Have ${aOrAn(randomAnimalName)} ${randomAnimalName}.`}</Typography>
        <Button
          variant="outlined"
          className={`${baseStyles['m-t-30']} ${baseStyles['btn-pill']}`}
          startIcon={<FontAwesomeIcon icon={faObjectsColumn} />}
          onClick={handleClickDashboard}
        >
          MY DASHBOARD
        </Button>
      </div>
    );
  }

  function renderStart() {
    return (
      <div className={[styles['center-col'], baseStyles['m-t-90']].join(' ')}>
        <img src={ImageLogo} alt="logo" />
        {user?.firstName ? (
          <Typography variant="h2">{`Welcome ${user.firstName} ${user.lastName}`}</Typography>
        ) : (
          <Typography variant="h2">{`Welcome ${student.firstName}`}</Typography>
        )}
        <Typography variant="h4">{assignment.assignment.name}</Typography>
        <Fab onClick={handleClickStart} size="large" className={styles.fabRoot}>
          <Typography className="clr-white fnt-bold">START</Typography>
        </Fab>
      </div>
    );
  }

  function renderExercise() {
    const type = assignment.type === 'LESSON' ? activeExercise.type : exercise.type;
    const props = {
      mode: 'LESSON', // Prevents exercise from rendering its primary app bar
      exercise: assignment.type === 'LESSON' ? activeExercise : exercise,
    };

    if ([APP_READING, APP_ASSESSMENT].includes(type)) return <PDFViewer {...props} />;
    else if (type === APP_WORD_WORK) return <WordWorkApp {...props} />;
    else if (type === APP_WORD_SORT) return <WordSortApp {...props} />;
    else if (type === APP_WORD_DIVISION) return <WordDivisionApp {...props} />;
    else if (type === APP_WRITING) return <Writing {...props} />;
    else return <div>APP TYPE {type} NOT SUPPORTED</div>;
  }

  return (
    <>
      <div className={styles.container}>
        {mode !== MODE_ASSIGNMENT && renderEmptyAppBar()}
        {mode === MODE_ASSIGNMENT && exercise && renderExerciseAppBar(exercise)}
        {mode === MODE_ASSIGNMENT && activeExercise && <PrimaryAppBar showDoneButton={!isLessonComplete} />}

        {mode === MODE_LOADING && (
          <div className={baseStyles['progress-container']}>
            <CircularProgress />
          </div>
        )}

        {mode === MODE_COMPLETE && renderComplete()}
        {mode === MODE_START && renderStart()}
        {mode === MODE_ASSIGNMENT && renderExercise()}
      </div>
    </>
  );
}
